import * as React from 'react';

import {TypeImplementation} from '@packages/systems/wfdl';
import {EEnum, TEnum} from '@packages/systems/wfdl-ast';

import {
  Select,
  Option,
  Group,
} from '@packages/systems/design-system/WebflowSelect';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {Note} from '@packages/systems/spring';

import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';

import {
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';

import type {BlockElementTag} from '@packages/systems/core/constants/BlockElementTags';

type Value = {
  id: BlockElementTag;
  label: string;
  note: string;
  noteLines: 1 | 2 | 3 | 4;
};

const DEFAULT_TAG: Value = {
  id: 'div',
  label: 'Div',
  note: 'The default, non-semantic tag',
  noteLines: 2,
};

const AVAILABLE_TAGS: Array<Value> = [
  DEFAULT_TAG,
  {
    id: 'header',
    label: 'Header',
    note: 'Specifies a header for a document or section',
    noteLines: 2,
  },
  {
    id: 'footer',
    label: 'Footer',
    note: 'Defines a footer for a document or section',
    noteLines: 2,
  },
  {
    id: 'nav',
    label: 'Nav',
    note: 'Defines a set of navigation links, such as those in your main site nav',
    noteLines: 3,
  },
  {
    id: 'main',
    label: 'Main',
    note: 'Specifies the main content of a document',
    noteLines: 2,
  },
  {
    id: 'section',
    label: 'Section',
    note: 'Defines a section of a document',
    noteLines: 2,
  },
  {
    id: 'article',
    label: 'Article',
    note: 'Defines an article',
    noteLines: 1,
  },
  {
    id: 'aside',
    label: 'Aside',
    note: 'Defines content aside from the page content, such as a sidebar',
    noteLines: 3,
  },
  {
    id: 'address',
    label: 'Address',
    note: 'Defines contact information for a person or people, or for an organization',
    noteLines: 3,
  },
  {
    id: 'figure',
    label: 'Figure',
    note: 'Specifies self-contained content, like illustrations, diagrams, photos, code listings, etc.',
    noteLines: 4,
  },
];

const selectNoteStyle = {
  boxShadow: 'rgb(51, 51, 51) 0px -1px 0px',
} as const;

const getCustomNoteHeight = (value: Value): number | undefined => {
  switch (value.noteLines) {
    case 4:
      return 87;
    case 3:
      return 71;
    case 2:
      return 55;
    case 1:
      return 39;
    default:
      return undefined;
  }
};

const TAGS_ENUM_VALUES = AVAILABLE_TAGS.reduce<Record<string, any>>(
  (acc, curr) => {
    acc[curr.id] = curr.label;
    return acc;
  },
  {}
);

export const TagsDropdown = TypeImplementation.Alias({
  type: TEnum(TAGS_ENUM_VALUES),
  render: function RenderTagsDropdown({expr, dispatch}) {
    const strippedExpr = stripAnn(expr);
    const initialId = ContextualExpressions.getEnum(strippedExpr);
    const [selectedValue, setSelectedValue] = React.useState<Value>(
      () => AVAILABLE_TAGS.find(({id}) => id === initialId) ?? DEFAULT_TAG
    );
    const {env} = useExpressionEditorImplementationContext();
    const {type} = getAnn(expr);

    if (type === null) {
      throw Error('Expected type from AnnotatedExpression to be defined');
    }

    const onChange = (nextValue: {
      id: BlockElementTag;
      label: string;
      note: string;
      noteLines: 1 | 2 | 3 | 4;
    }) => {
      setSelectedValue(nextValue);
      dispatch(updateExpressionAction(EEnum(nextValue.id)));
    };

    return (
      <TooltipTrigger
        content="These tags help both browsers and developers understand an element's content."
        alternate
      >
        <Select
          id="tags-select-menu"
          aria-label="Select element tag"
          value={selectedValue}
          onChange={(value, {changeType}) => {
            if (changeType === 'changeStart') {
              env.transaction.begin();
            } else if (changeType === 'changeEnd') {
              env.transaction.commit();
            } else if (changeType === 'changeCancel') {
              env.transaction.abort();
            } else {
              onChange(value);
            }
          }}
        >
          {({open}) => {
            // If the managed tag state changed _outside_ of the dropdown (determined
            // by the select menu being closed), sync the local state
            if (open === false && selectedValue.id !== initialId) {
              setSelectedValue(
                AVAILABLE_TAGS.find(({id}) => id === initialId) ?? DEFAULT_TAG
              );
            }

            return (
              <>
                <Group>
                  {AVAILABLE_TAGS.map((option) => (
                    <Option
                      key={option.id}
                      value={option}
                      label={option.label}
                    />
                  ))}
                </Group>
                <Group customHeight={getCustomNoteHeight(selectedValue)}>
                  <Option
                    disabled
                    selectable={false}
                    value={null}
                    style={selectNoteStyle}
                    label={
                      <Note isNested colorVariant="default" hasIcon={false}>
                        {selectedValue.note}
                      </Note>
                    }
                  />
                </Group>
              </>
            );
          }}
        </Select>
      </TooltipTrigger>
    );
  },
});
